import type { JSX } from 'react';
import { FormattedMessage } from 'react-intl';
import { Link, useNavigate } from 'react-router';
import { tss } from 'tss-react/mui';
import { H1 } from '../../components/H1/index.ts';
import { H2 } from '../../components/H2/index.ts';
import { Paragraph } from '../../components/Paragraph/index.ts';
import Button from '../../components/Button/index.tsx';
import Logo from '../../components/Logo/index.tsx';
import { PageFooter } from '../Page/PageFooter.tsx';

// Our page or content not found page templates/layouts only differ in main content.
// Hence, we can only change its "main content".
export function PageNotFoundLayout({
    hiddenHeader = false,
    hiddenFooter = false,
    hiddenStatus = false,
    title,
    description,
    actionButtons,
    fullHeight = false,
    className,
    ...rootProps
}: {
    hiddenHeader?: boolean;
    hiddenFooter?: boolean;
    hiddenStatus?: boolean;
    fullHeight?: boolean;
    title?: JSX.Element;
    description?: JSX.Element;
    actionButtons?: JSX.Element;
} & Omit<JSX.IntrinsicElements['div'], 'title'>) {
    const { classes, cx } = usePageNotFoundStyles({ fullHeight });
    const navigate = useNavigate();
    return (
        <div {...rootProps} className={cx(classes.pageNotFound, className)}>
            <header className={classes.header}>
                {!hiddenHeader && (
                    <Link to="/">
                        <Logo />
                    </Link>
                )}
            </header>
            <div role="alert" className={classes.content}>
                {!hiddenStatus && (
                    <H1 className={classes.expressionText}>404</H1>
                )}
                <H2>
                    {title ? (
                        title
                    ) : (
                        <FormattedMessage
                            defaultMessage="Page not found"
                            description="Error page title"
                        />
                    )}
                </H2>
                <Paragraph className={classes.description}>
                    {description ? (
                        description
                    ) : (
                        <FormattedMessage
                            defaultMessage="The link may be broken, or the page may have been removed. Check to see if the link you're trying to open is correct."
                            description="Error page description"
                        />
                    )}
                </Paragraph>
                {actionButtons ? (
                    actionButtons
                ) : (
                    <Paragraph>
                        <FormattedMessage
                            defaultMessage="<Button>Go to homepage</Button>"
                            description="Tip description"
                            values={{
                                Button: (chunks) => (
                                    <Button
                                        onClick={() => {
                                            navigate('/');
                                        }}
                                    >
                                        {chunks}
                                    </Button>
                                ),
                            }}
                        />
                    </Paragraph>
                )}
            </div>
            {!hiddenFooter && <PageFooter />}
        </div>
    );
}

const usePageNotFoundStyles = tss
    .withParams<{ fullHeight: boolean }>()
    .create(({ theme, fullHeight }) => ({
        pageNotFound: {
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            height: fullHeight ? '100vh' : '100%',
        },
        description: {
            maxWidth: 600,
            margin: 'auto',
            marginBottom: 16,
        },
        expressionText: {
            fontSize: 90,
            marginBottom: 70,

            [theme.breakpoints.up('sm')]: {
                fontSize: 120,
            },
        },
        header: {
            alignSelf: 'center',
            margin: '3em 0 5em',
        },
        content: {
            flexGrow: 1,
            alignSelf: 'center',
            margin: '0 3em 3em',
            textAlign: 'center',
        },
    }));
